import { sql } from 'drizzle-orm';
import { check, integer, pgEnum, pgTable, boolean, numeric, text } from 'drizzle-orm/pg-core';
import * as z from 'zod';
import { timestamps } from './utils/index.js';
import { userActions } from './utils/user-actions.js';
export const BSAFormula = z.enum(['DU_BOIS', 'MOSTELLER', 'HAYCOCK']);
export const bsaFormulaEnum = pgEnum('bsa_formula', [
    BSAFormula.enum.DU_BOIS,
    BSAFormula.enum.MOSTELLER,
    BSAFormula.enum.HAYCOCK,
]);
export const IBWFormula = z.enum(['DEVINE', 'ROBINSON', 'MILLER']);
export const ibwFormulaEnum = pgEnum('ibw_formula', [
    IBWFormula.enum.DEVINE,
    IBWFormula.enum.ROBINSON,
    IBWFormula.enum.MILLER,
]);
export const Language = z.enum(['EN', 'DE', 'RO', 'PL']);
export const LanguageEnum = pgEnum('language', [Language.enum.EN, Language.enum.DE, Language.enum.RO, Language.enum.PL]);
export const PatientNameOrder = z.enum(['LAST_NAME_FIRST', 'FIRST_NAME_LAST']);
export const PatientNameOrderEnum = pgEnum('patient_name_order', [
    PatientNameOrder.enum.LAST_NAME_FIRST,
    PatientNameOrder.enum.FIRST_NAME_LAST,
]);
export const PatientIdentifier = z.enum(['PATIENT_NUMBER', 'CASE_NUMBER', 'INSURANCE_NUMBER']);
// `patient_identifier_type` avoids a clash with the `patient_identifier` table's row type
export const PatientIdentifierEnum = pgEnum('patient_identifier_type', [
    PatientIdentifier.enum.PATIENT_NUMBER,
    PatientIdentifier.enum.CASE_NUMBER,
    PatientIdentifier.enum.INSURANCE_NUMBER,
]);
export const AdministrationMode = z.enum(['SCAN', 'MANUAL']);
export const AdministrationModeEnum = pgEnum('administration_mode', [
    AdministrationMode.enum.SCAN,
    AdministrationMode.enum.MANUAL,
]);
export const LabelVolume = z.enum(['TOTAL_VOLUME', 'SUBSTANCE_VOLUME', 'NONE']);
export const LabelVolumeEnum = pgEnum('label_volume', [
    LabelVolume.enum.TOTAL_VOLUME,
    LabelVolume.enum.SUBSTANCE_VOLUME,
    LabelVolume.enum.NONE,
]);
/** paper size of the label printer roll, width × height (CY-421) */
export const LabelSize = z.enum(['SIZE_100X90', 'SIZE_70X60']);
export const LabelSizeEnum = pgEnum('label_size', [LabelSize.enum.SIZE_100X90, LabelSize.enum.SIZE_70X60]);
export const ConcentrationBasis = z.enum(['CARRIER_VOLUME', 'TOTAL_VOLUME']);
export const ConcentrationBasisEnum = pgEnum('concentration_basis', [
    ConcentrationBasis.enum.CARRIER_VOLUME,
    ConcentrationBasis.enum.TOTAL_VOLUME,
]);
export const settings = pgTable('settings', {
    id: integer('id').primaryKey().default(1), // use integer insead of uuid because there will be only one row
    bsaFormula: bsaFormulaEnum('bsa_formula').notNull().default(BSAFormula.enum.DU_BOIS),
    ibwFormula: ibwFormulaEnum('ibw_formula').notNull().default(IBWFormula.enum.DEVINE),
    activeProtocolWarning: boolean('active_protocol_warning').notNull().default(true),
    doctorRelease: boolean('doctor_release').notNull().default(true),
    deliveryAutoRelease: boolean('delivery_auto_release').notNull().default(false),
    autIdemLogic: boolean('aut_idem_logic').notNull().default(false),
    validationBypass: boolean('validation_bypass').notNull().default(false),
    // calculations: prescribe by trade name by default (active substance prescribing is the fallback)
    prescribeByTradeName: boolean('prescribe_by_trade_name').notNull().default(false),
    // interfaces: hospital interface. When off, the patient/stock can only arrive through the interface
    allowManualPatients: boolean('allow_manual_patients').notNull().default(true),
    allowManualStock: boolean('allow_manual_stock').notNull().default(true),
    // workflows
    markAdministeredOnDispatch: boolean('mark_administered_on_dispatch').notNull().default(false),
    administrationMode: AdministrationModeEnum('administration_mode').notNull().default(AdministrationMode.enum.SCAN),
    weightTolerance: numeric('weightTolerance').notNull().default('3'),
    // confirm a weighing step on its own once the stable weight has been in tolerance for this long (CY-422)
    weightAutoConfirm: boolean('weight_auto_confirm').notNull().default(true),
    weightAutoConfirmDelayMs: integer('weight_auto_confirm_delay_ms').notNull().default(2000),
    // ui & branding
    language: LanguageEnum('language').notNull().default('EN'),
    logoUrl: text('logo_url'),
    loginText: text('login_text'),
    patientNameOrder: PatientNameOrderEnum('patient_name_order')
        .notNull()
        .default(PatientNameOrder.enum.LAST_NAME_FIRST),
    patientIdentifier: PatientIdentifierEnum('patient_identifier')
        .notNull()
        .default(PatientIdentifier.enum.PATIENT_NUMBER),
    // rest vials
    allowVialReuse: boolean('allow_vial_reuse').notNull().default(true), // allows for reuse of leftover substance for another patient
    useVialSource: boolean('use_vial_source').notNull().default(true), // enables tagging the source of the drug, for export to hospital billing system
    minimumSaveableVolume: numeric('minimum_saveable_volume').notNull().default('10'), // minimum % of vial volume required to create a rest vial
    licence: text('licence'),
    licenceTampered: boolean('licence_tampered'),
    dayZeroProtocol: boolean('day_zero_protocol').notNull().default(true),
    // show the hospital patient id in the patient screen header
    showHospitalPatientId: boolean('show_hospital_patient_id').notNull().default(true),
    useScales: boolean('use_scales').notNull().default(true),
    // stores the scale's internal memory number (Alibi) with every weighing
    recordScaleMemoryNumbers: boolean('record_scale_memory_numbers').notNull().default(true),
    // stop-gap for sites without solvent stock: solvents are listed on the picking list but never checked, allocated or deducted
    skipSolventStockCheck: boolean('skip_solvent_stock_check').notNull().default(false),
    // security: sessions end after this many minutes without activity
    sessionDurationMinutes: integer('session_duration_minutes').notNull().default(10080),
    // printouts: label content
    prescribingDoctor: boolean('prescribing_doctor').notNull().default(true),
    hospitalNumber: boolean('hospital_number').notNull().default(true),
    customerName: boolean('customer_name').notNull().default(true),
    wardNextToCustomerName: boolean('ward_next_to_customer_name').notNull().default(false),
    manufacturer: boolean('manufacturer').notNull().default(true),
    tradeNameBeforeSubstance: boolean('trade_name_before_substance').notNull().default(false),
    concentration: boolean('concentration').notNull().default(true),
    actualQuantity: boolean('actual_quantity').notNull().default(true),
    preparedBy: boolean('prepared_by').notNull().default(true),
    pharmacyNameAndAddress: boolean('pharmacy_name_and_address').notNull().default(true),
    additionalLabelBarcode: boolean('additional_label_barcode').notNull().default(false),
    labelVolume: LabelVolumeEnum('label_volume').notNull().default(LabelVolume.enum.TOTAL_VOLUME),
    bandTextCytotoxic: text('band_text_cytotoxic').notNull().default('CYTOTOXIC'),
    bandTextNonCytotoxic: text('band_text_non_cytotoxic').notNull().default('NON-CYTOTOXIC'),
    labelCopies: integer('label_copies').notNull().default(1),
    labelSize: LabelSizeEnum('label_size').notNull().default(LabelSize.enum.SIZE_100X90),
    // printouts: preparation report
    printReportAutomatically: boolean('print_report_automatically').notNull().default(false),
    includeLabelCopy: boolean('include_label_copy').notNull().default(false),
    includeEndControlSignatures: boolean('include_end_control_signatures').notNull().default(false),
    showScaleMemoryNumbers: boolean('show_scale_memory_numbers').notNull().default(true),
    showProductCodeColumn: boolean('show_product_code_column').notNull().default(true),
    concentrationBasis: ConcentrationBasisEnum('concentration_basis')
        .notNull()
        .default(ConcentrationBasis.enum.CARRIER_VOLUME),
    // printouts: delivery and release
    deliveryNoteCopies: integer('delivery_note_copies').notNull().default(3),
    releaseApprovalRequired: boolean('release_approval_required').notNull().default(true),
    signatureLinesOnPrintouts: boolean('signature_lines_on_printouts').notNull().default(true),
    ...timestamps(),
    ...userActions(),
}, table => [check('single_row', sql `${table.id} = 1`)]);
//# sourceMappingURL=settings.js.map